import { useEffect, useState } from 'react';
import type { DexStatus } from '@memepad/shared';
import { getRecentTokens } from '../lib/api';
import { useAppStore } from '../store';
import { TokenCard } from '../components/TokenCard';

/**
 * CC-025 follow-up (Home feed progress-bar + targetTon fix):
 *
 *   The previous Home.tsx had a local `TokenRow` with two display
 *   bugs (BigInt int-division collapsing sub-TON values to 0, and a
 *   hardcoded `targetTon = 1500`). Both surfaces — `myTokens` and the
 *   public recent feed — used the same broken row, so users saw
 *   "0 / 1500 TON" / 0% bar on every dev token. The fix:
 *
 *     • Lift the row into `components/TokenCard.tsx` so both lists
 *       render identical visuals.
 *     • TokenCard sources `targetTon` from the API (added to
 *       `RecentTokenRow` and `MeResponse.myTokens` in the same PR).
 *     • The conversion runs through `lib/displayMath.ts` so future
 *       progress bars can reuse it (e.g. Token detail header).
 */

// Shape returned by GET /api/tokens/recent. Kept here (rather than
// exported from @memepad/shared) because the prod and dev feeds use
// slightly different supersets — the actual on-wire JSON may carry more
// fields than the UI consumes.
interface RecentToken {
  ticker: string;
  masterAddr: string;
  imageCid: string;
  realTonReserves: string;
  targetTon: string; // CC-025: added to the API response
  isGraduated: boolean;
  dexStatus: DexStatus | null; // CC-027: drives the migration badge
}

export function HomeRoute() {
  const me = useAppStore((s) => s.me);
  const [recent, setRecent] = useState<RecentToken[] | null>(null);

  useEffect(() => {
    // CC-052-F-1: the discovery feed must not surface is_test_token=true
    // tokens — those are dev-factory launches (e.g. the 27 cohort-1 dev
    // tokens), not for the production-style Mini App UI.
    getRecentTokens({ limit: 20, includeTest: import.meta.env.VITE_INCLUDE_TEST_TOKENS === 'true' })
      .then((r) => setRecent(r.tokens as RecentToken[]))
      .catch(console.error);
  }, []);

  return (
    <div className="container vgap-16">
      <header>
        <h1 style={{ margin: 0, fontSize: 24 }}>Blinkr</h1>
        <div className="muted">Запускай мемкоины на TON. Без кода и без затрат.</div>
      </header>

      {me?.myTokens && me.myTokens.length > 0 && (
        <section>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>Мои токены</h2>
          <div className="vgap-8">
            {me.myTokens.map((t) => (
              <TokenCard
                key={t.masterAddr}
                ticker={t.ticker}
                masterAddr={t.masterAddr}
                imageCid={t.imageCid}
                realTonReserves={t.realTonReserves}
                targetTon={t.targetTon}
                isGraduated={t.isGraduated}
                dexStatus={t.dexStatus}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Недавние запуски</h2>
        <div className="vgap-8">
          {recent === null && <Skeletons />}
          {recent !== null && recent.length === 0 && (
            <div className="muted">Пока запусков нет. Будь первым — тегни бота!</div>
          )}
          {recent?.map((t) => (
            <TokenCard
              key={t.masterAddr}
              ticker={t.ticker}
              masterAddr={t.masterAddr}
              imageCid={t.imageCid}
              realTonReserves={t.realTonReserves}
              targetTon={t.targetTon}
              isGraduated={t.isGraduated}
              dexStatus={t.dexStatus}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function Skeletons() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="card row">
          <div className="skeleton" style={{ width: 48, height: 48 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 14, width: '40%' }} />
            <div className="skeleton" style={{ height: 6, width: '100%', marginTop: 12 }} />
          </div>
        </div>
      ))}
    </>
  );
}
